import _ from 'lodash'
import { useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'
import { skipToken } from '@reduxjs/toolkit/query'

import { ObjectType } from '@/core/models/document.model'
import { useGetExpertiseQuery } from '@/core/services/expertise'
import { useAddFileMutation, useDeleteFilesMutation, useUpdateFilenameMutation } from '@/core/services/file'

import { useAppSelector } from '@/redux/rootReducer'
import { selectAuthUser } from '@/redux/reducers/auth.reducer'

import { getApiErrorMessage } from '@/utils/errors'

import Documents, { DocumentsProps } from '../components/Documents'

type DocumentsContainerProps = Omit<DocumentsProps, 'documents' | 'isDeleting' | 'onDelete' | 'onUpload'> & {}

export function DocumentsContainer(props: DocumentsContainerProps & {expertiseId?: string}) {
  const { expertiseId: paramExpertiseId } = useParams()
  const expertiseId = props.expertiseId || paramExpertiseId
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation()

  const authUser = useAppSelector(selectAuthUser)

  const { data: expertise } = useGetExpertiseQuery(expertiseId ?? skipToken)
  const [addFile] = useAddFileMutation()
  const [deleteFiles, { isLoading: isDeletingFiles }] = useDeleteFilesMutation()
  const [updateFilename] = useUpdateFilenameMutation()

  const documents = useMemo(
    () =>
      _.map(expertise?.files, (file) => ({
        filename: file.filename,
        key: file.key,
        url: file.url,
        canDelete: authUser?._id === expertise?.createdBy._id ? true : authUser?._id === file.uploadedBy,
      })),
    [authUser, expertise]
  )

  const handleDelete = async (deletedDocuments: DocumentsProps['documents']) => {
    if (!expertiseId || !deletedDocuments.length) return

    const files = _.map(deletedDocuments, (document) => ({
      filename: document.filename,
      key: document.key,
      objectType: 'expertise' as ObjectType,
    }))
    try {
      await deleteFiles({ files, objectId: expertiseId, objectType: 'expertise' }).unwrap()
      enqueueSnackbar(t('notification.document.delete.success', { count: files.length }), { variant: 'success' })
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, t('notification.document.delete.error')), { variant: 'error' })
    }
  }

  const handleSaveFilename = async (newFilename: string, key: string) => {
    const file = _.find(expertise?.files, (file) => file.key === key)
    if (!file || !expertiseId) return
    try {
      await updateFilename({ file: _.omit(file, 'url'), newFilename, objectId: expertiseId, objectType: 'expertise' }).unwrap()
      enqueueSnackbar(t('notification.document.updated.success'), { variant: 'success' })
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, t('notification.document.updated.error')), { variant: 'error' })
    }
  }

  const handleUpload = async (file: File) => {
    if (!expertiseId) return
    try {
      await addFile({ file, objectType: 'expertise', objectId: expertiseId }).unwrap()
      enqueueSnackbar(t('notification.document.create.success'), { variant: 'success' })
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, t('notification.document.create.error')), { variant: 'error' })
    }
  }

  return (
    <Documents
      {...props}
      isDeleting={isDeletingFiles}
      documents={documents}
      onDelete={handleDelete}
      onSaveFileAnnotation={handleUpload}
      onSaveFileName={handleSaveFilename}
      onUpload={handleUpload}
    />
  )
}

export default DocumentsContainer
